// Where private notes show in a file (docs/design.md "Notes"). A note names
// content, `(path, side, blob, lines)`, as a review mark's anchor does, so
// it shows wherever its blob does, and on a later (or earlier) blob of the
// file where the line map between the two blobs (`EngineApi.lineMap`, kept
// by `review/lineMaps.ts`) takes its lines. No layout enters: the same
// notes land on the same lines under every algorithm, range and fold.
import type { Note } from '../api/server';
import type { Side } from '../engine/types';
import { lineMapKey, type ReviewSide } from './model';

/** A note as a view shows it: on side `side`, lines `from..to` there. */
export interface PlacedNote {
  note: Note;
  side: Side;
  from: number;
  to: number;
  /** Shown through a line map: written on another blob of the file. */
  carried: boolean;
}

/** Where a file's notes go. */
export interface NotePlacement {
  /** The notes shown on lines, by side and first line, oldest first among equals. */
  placed: PlacedNote[];
  /** Notes on another blob of the file none of whose lines are in the blob shown. */
  lost: Note[];
  /** Line maps not computed yet that notes wait on, each once. */
  waiting: { from: string; to: string }[];
}

/** One file as a view shows it: each side's path, blob and former paths. */
export interface NoteFile {
  old: ReviewSide | null;
  new: ReviewSide | null;
}

const other = (s: Side): Side => (s === 'old' ? 'new' : 'old');

function onPath(note: Note, at: ReviewSide | null): boolean {
  return !!at && (note.path === at.path || (at.renamedFrom ?? []).includes(note.path));
}

/**
 * Where `notes` go in `file`. A note on a blob the file shows goes on that
 * side, at its own lines: on its own side when both sides show the blob.
 * A note on another blob of the file (at its path or a former one) is
 * carried to its own side (the other, when the file lacks that side) by
 * the line map from its blob, and spans the lines its lines land on; with
 * none landing it is `lost`, and with no line map yet it waits for one.
 */
export function placeNotes(notes: readonly Note[], file: NoteFile, lineMaps: ReadonlyMap<string, readonly number[]>): NotePlacement {
  const placed: PlacedNote[] = [];
  const lost: Note[] = [];
  const waiting = new Map<string, { from: string; to: string }>();
  for (const note of notes) {
    const own = file[note.side];
    const opposite = file[other(note.side)];
    if (!onPath(note, own) && !onPath(note, opposite)) continue;
    // A side's blob, or another ID of its text (docs/design.md "Blob IDs").
    const shows = (at: ReviewSide | null) => !!at && (at.blob === note.blob || !!at.aliases?.includes(note.blob));
    const direct = shows(own) ? note.side : shows(opposite) ? other(note.side) : null;
    if (direct) {
      placed.push({ note, side: direct, from: note.start_line, to: note.end_line, carried: false });
      continue;
    }
    const side = own ? note.side : other(note.side);
    const target = file[side]!;
    const key = lineMapKey(note.blob, target.blob);
    const map = lineMaps.get(key);
    if (!map) {
      waiting.set(key, { from: note.blob, to: target.blob });
      continue;
    }
    const lines: number[] = [];
    for (let l = note.start_line; l <= note.end_line; l++) {
      const to = map[l - 1] ?? 0;
      if (to) lines.push(to);
    }
    if (!lines.length) lost.push(note);
    else placed.push({ note, side, from: Math.min(...lines), to: Math.max(...lines), carried: true });
  }
  placed.sort((a, b) => a.side.localeCompare(b.side) || a.from - b.from || a.note.id - b.note.id);
  return { placed, lost, waiting: [...waiting.values()] };
}
